import { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import type { CancelTokenSource } from 'axios'
import {
  createReportFactoryJob,
  getReportFactoryJob,
  ReportFactoryError,
} from './reportFactoryService'
import type {
  ReportFactoryRequest,
  ReportFactorySucceededJob,
} from './reportFactoryTypes'
import { createIdempotencyKey } from './utils/createIdempotencyKey'
import { downloadReportFile, ReportFileError } from './utils/downloadReportFile'

type Phase = 'idle' | 'creating' | 'polling' | 'downloading'
type Attempt = { request: ReportFactoryRequest; idempotencyKey: string }
type Retry =
  | { kind: 'create'; attempt: Attempt }
  | { kind: 'status' | 'download'; jobId: string }
export type ExportFeedback = {
  type: 'info' | 'success' | 'warning' | 'error'
  key: string
  portfolioNames?: string
}

const getNoDataPortfolioNames = (portfolios: unknown[]): string =>
  Array.from(
    new Set(
      portfolios
        .filter((name): name is string => typeof name === 'string')
        .map((name) => name.trim())
        .filter(Boolean)
    )
  ).join(', ')
type State = {
  phase: Phase
  feedback?: ExportFeedback
  retry?: Retry
  downloadJobId?: string
}
type ExportController = State & {
  busy: boolean
  conditionsLocked: boolean
  isLocked: () => boolean
  submit: (request: ReportFactoryRequest) => Promise<void>
  downloadExport: () => Promise<void>
  retryExport: () => Promise<void>
  startNewReport: () => void
  clearFeedback: () => void
}

const POLL_INTERVAL = 3000
const WAIT_LIMIT = 10 * 60 * 1000
const DOWNLOAD_TIMEOUT = 60 * 1000

const ERROR_KEYS: Record<string, string> = {
  API_URL_NOT_CONFIGURED: 'hkReport.error.connectionSettings',
  TOKEN_NOT_AVAILABLE: 'hkReport.error.authentication',
  INVALID_RESPONSE: 'hkReport.error.invalidResponse',
  INVALID_INPUT: 'hkReport.error.invalidInput',
  INVALID_IDEMPOTENCY_KEY: 'hkReport.error.request',
  CONFIG_VERSION_MISMATCH: 'hkReport.feedback.configUpdated',
  IDEMPOTENCY_CONFLICT: 'hkReport.error.idempotencyConflict',
  QUEUE_DISPATCH_UNCONFIRMED: 'hkReport.error.requestUnconfirmed',
  EXPORT_SERVICE_UNAVAILABLE: 'hkReport.error.serviceUnavailable',
  NETWORK_ERROR: 'hkReport.error.network',
  EXPORT_FAILED: 'hkReport.error.exportFailed',
  QUEUE_DELAY_TIMEOUT: 'hkReport.error.queueTimeout',
  EXECUTION_TIMEOUT: 'hkReport.error.executionTimeout',
  EXECUTION_BUDGET_EXCEEDED: 'hkReport.error.executionBudget',
}

export const getReportErrorKey = (error: unknown): string => {
  if (error instanceof ReportFactoryError) {
    if (Object.prototype.hasOwnProperty.call(ERROR_KEYS, error.code))
      return ERROR_KEYS[error.code]
    if (error.status === 401) return 'hkReport.error.authentication'
    if (error.status === 403) return 'hkReport.error.forbidden'
    if (error.status === 404) return 'hkReport.error.jobNotFound'
    if (error.status === 410) return 'hkReport.error.jobExpired'
  }
  return 'hkReport.error.request'
}

export const useReportFactoryExport = (
  refreshConfig: () => Promise<void>
): ExportController => {
  const [state, setState] = useState<State>({ phase: 'idle' })
  const mounted = useRef(true)
  const active = useRef(false)
  const generation = useRef(0)
  const retryRef = useRef<Retry>()
  const completedJobId = useRef<string>()
  const requestCancellation = useRef<CancelTokenSource>()
  const downloadCancellation = useRef<AbortController>()
  const pollTimer = useRef<ReturnType<typeof setTimeout>>()
  const deadlineTimer = useRef<ReturnType<typeof setTimeout>>()
  const downloadTimer = useRef<ReturnType<typeof setTimeout>>()
  const refreshConfigRef = useRef(refreshConfig)
  refreshConfigRef.current = refreshConfig

  const clearWork = useCallback(() => {
    if (pollTimer.current !== undefined) clearTimeout(pollTimer.current)
    if (deadlineTimer.current !== undefined) clearTimeout(deadlineTimer.current)
    if (downloadTimer.current !== undefined) clearTimeout(downloadTimer.current)
    pollTimer.current = undefined
    deadlineTimer.current = undefined
    downloadTimer.current = undefined
    requestCancellation.current?.cancel()
    requestCancellation.current = undefined
    downloadCancellation.current?.abort()
    downloadCancellation.current = undefined
  }, [])

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      active.current = false
      generation.current += 1
      clearWork()
    }
  }, [clearWork])

  const isCurrent = (operation: number) =>
    mounted.current && active.current && operation === generation.current

  const finish = (
    operation: number,
    feedback: ExportFeedback,
    retry?: Retry
  ) => {
    if (!isCurrent(operation)) return
    active.current = false
    clearWork()
    retryRef.current = retry
    setState({
      phase: 'idle',
      feedback,
      retry,
      downloadJobId: completedJobId.current,
    })
  }

  const setPhase = (operation: number, phase: Phase) => {
    if (isCurrent(operation))
      setState({ phase, downloadJobId: completedJobId.current })
  }

  const begin = (phase: Phase): number | undefined => {
    if (!mounted.current || active.current) return undefined
    clearWork()
    active.current = true
    generation.current += 1
    retryRef.current = undefined
    setState({ phase, downloadJobId: completedJobId.current })
    return generation.current
  }

  const requestOptions = () => {
    const cancellation = axios.CancelToken.source()
    requestCancellation.current = cancellation
    return { cancelToken: cancellation.token }
  }

  const finishFailedJob = (operation: number, failureCode: string) => {
    if (!isCurrent(operation)) return
    completedJobId.current = undefined
    finish(operation, {
      type: 'error',
      key: Object.prototype.hasOwnProperty.call(ERROR_KEYS, failureCode)
        ? ERROR_KEYS[failureCode]
        : 'hkReport.error.exportFailed',
    })
  }

  const finishReadyJob = (
    job: ReportFactorySucceededJob,
    operation: number
  ) => {
    if (!isCurrent(operation)) return
    completedJobId.current = job.jobId
    const portfolioNames = getNoDataPortfolioNames(job.noDataPortfolios)
    finish(operation, {
      type: job.noDataPortfolios.length ? 'warning' : 'info',
      key: job.noDataPortfolios.length
        ? portfolioNames
          ? 'hkReport.feedback.readyNoDataNamed'
          : 'hkReport.feedback.readyNoData'
        : 'hkReport.feedback.ready',
      portfolioNames,
    })
  }

  const downloadSucceededJob = async (
    job: ReportFactorySucceededJob,
    operation: number,
    fresh = false
  ): Promise<void> => {
    if (!isCurrent(operation)) return
    if (pollTimer.current !== undefined) clearTimeout(pollTimer.current)
    if (deadlineTimer.current !== undefined) clearTimeout(deadlineTimer.current)
    pollTimer.current = undefined
    deadlineTimer.current = undefined
    setPhase(operation, 'downloading')
    if (!fresh && Date.parse(job.urlExpiresAt) <= Date.now()) {
      await refreshDownload(job.jobId, operation)
      return
    }
    const cancellation = new AbortController()
    downloadCancellation.current = cancellation
    // S3の応答やファイル読取が止まっても、同じジョブから再取得できるようにする。
    downloadTimer.current = setTimeout(
      () =>
        finish(
          operation,
          { type: 'error', key: 'hkReport.error.download' },
          { kind: 'download', jobId: job.jobId }
        ),
      DOWNLOAD_TIMEOUT
    )
    try {
      await downloadReportFile(job, {
        signal: cancellation.signal,
        isCurrent: () => isCurrent(operation),
      })
      const portfolioNames = getNoDataPortfolioNames(job.noDataPortfolios)
      finish(operation, {
        type: job.noDataPortfolios.length ? 'warning' : 'success',
        key: job.noDataPortfolios.length
          ? portfolioNames
            ? 'hkReport.feedback.noDataNamed'
            : 'hkReport.feedback.noData'
          : 'hkReport.feedback.downloaded',
        portfolioNames,
      })
    } catch (error) {
      if (!isCurrent(operation)) return
      finish(
        operation,
        {
          type: 'error',
          key:
            error instanceof ReportFileError && error.code === 'URL_EXPIRED'
              ? 'hkReport.error.urlExpired'
              : 'hkReport.error.download',
        },
        { kind: 'download', jobId: job.jobId }
      )
    }
  }

  const pollJob = async (jobId: string, operation: number): Promise<void> => {
    try {
      const job = await getReportFactoryJob(jobId, requestOptions())
      if (!isCurrent(operation)) return
      if (job.status === 'SUCCEEDED') {
        finishReadyJob(job, operation)
      } else if (job.status === 'FAILED') {
        finishFailedJob(operation, job.failureCode)
      } else {
        // 前のGETが完了してから次を予約し、重複したpollingを防ぐ。
        pollTimer.current = setTimeout(() => {
          if (isCurrent(operation)) void pollJob(jobId, operation)
        }, POLL_INTERVAL)
      }
    } catch (error) {
      if (!isCurrent(operation)) return
      const terminal =
        error instanceof ReportFactoryError &&
        (error.code === 'INVALID_RESPONSE' ||
          [400, 401, 403, 404, 410].includes(error.status || 0))
      if (terminal) completedJobId.current = undefined
      finish(
        operation,
        { type: 'error', key: getReportErrorKey(error) },
        terminal ? undefined : { kind: 'status', jobId }
      )
    }
  }

  const startPolling = async (
    jobId: string,
    operation: number
  ): Promise<void> => {
    if (!isCurrent(operation)) return
    setPhase(operation, 'polling')
    // 応答が返らないGETでも待機上限を保証する。
    deadlineTimer.current = setTimeout(
      () =>
        finish(
          operation,
          {
            type: 'warning',
            key: 'hkReport.error.waitLimit',
          },
          { kind: 'status', jobId }
        ),
      WAIT_LIMIT
    )
    await pollJob(jobId, operation)
  }

  const refreshDownload = async (
    jobId: string,
    operation: number
  ): Promise<void> => {
    if (!isCurrent(operation)) return
    setPhase(operation, 'downloading')
    try {
      const job = await getReportFactoryJob(jobId, requestOptions())
      if (!isCurrent(operation)) return
      if (job.status === 'SUCCEEDED') {
        await downloadSucceededJob(job, operation, true)
      } else if (job.status === 'FAILED') {
        finishFailedJob(operation, job.failureCode)
      } else {
        await startPolling(jobId, operation)
      }
    } catch (error) {
      if (!isCurrent(operation)) return
      const terminal =
        error instanceof ReportFactoryError &&
        (error.code === 'INVALID_RESPONSE' ||
          [400, 401, 403, 404, 410].includes(error.status || 0))
      if (terminal) completedJobId.current = undefined
      finish(
        operation,
        { type: 'error', key: getReportErrorKey(error) },
        terminal ? undefined : { kind: 'download', jobId }
      )
    }
  }

  const createJob = async (
    attempt: Attempt,
    operation: number
  ): Promise<void> => {
    try {
      const accepted = await createReportFactoryJob(
        attempt.request,
        attempt.idempotencyKey,
        requestOptions()
      )
      if (isCurrent(operation)) await startPolling(accepted.jobId, operation)
    } catch (error) {
      if (!isCurrent(operation)) return
      if (
        error instanceof ReportFactoryError &&
        error.code === 'CONFIG_VERSION_MISMATCH'
      ) {
        try {
          await refreshConfigRef.current()
          finish(operation, {
            type: 'warning',
            key: 'hkReport.feedback.configUpdated',
          })
        } catch (configError) {
          finish(operation, {
            type: 'error',
            key: getReportErrorKey(configError),
          })
        }
        return
      }
      const uncertain =
        error instanceof ReportFactoryError &&
        error.code !== 'EXPORT_SERVICE_UNAVAILABLE' &&
        ([
          'QUEUE_DISPATCH_UNCONFIRMED',
          'NETWORK_ERROR',
          'INVALID_RESPONSE',
        ].includes(error.code) ||
          (error.status || 0) >= 500)
      finish(
        operation,
        {
          type: 'error',
          key: uncertain
            ? 'hkReport.error.requestUnconfirmed'
            : getReportErrorKey(error),
        },
        uncertain ? { kind: 'create', attempt } : undefined
      )
    }
  }

  const submit = async (request: ReportFactoryRequest): Promise<void> => {
    if (active.current || retryRef.current?.kind === 'create') return
    completedJobId.current = undefined
    const operation = begin('creating')
    if (operation === undefined) return
    try {
      const attempt = {
        request: {
          ...request,
          portfolios: request.portfolios.map((item) => ({ ...item })),
        },
        idempotencyKey: createIdempotencyKey(),
      }
      await createJob(attempt, operation)
    } catch (error) {
      finish(operation, { type: 'error', key: getReportErrorKey(error) })
    }
  }

  const downloadExport = async (): Promise<void> => {
    const jobId = completedJobId.current
    if (!jobId) return
    const operation = begin('downloading')
    if (operation === undefined) return
    await refreshDownload(jobId, operation)
  }

  const retry = async (): Promise<void> => {
    const target = retryRef.current
    if (!target) return
    const operation = begin(
      target.kind === 'create'
        ? 'creating'
        : target.kind === 'status'
        ? 'polling'
        : 'downloading'
    )
    if (operation === undefined) return
    if (target.kind === 'create') await createJob(target.attempt, operation)
    else if (target.kind === 'status')
      await startPolling(target.jobId, operation)
    else await refreshDownload(target.jobId, operation)
  }

  const startNewReport = () => {
    if (active.current) return
    const wasUnconfirmed = retryRef.current?.kind === 'create'
    generation.current += 1
    clearWork()
    retryRef.current = undefined
    completedJobId.current = undefined
    setState({
      phase: 'idle',
      feedback: wasUnconfirmed
        ? {
            type: 'warning',
            key: 'hkReport.feedback.previousRequestUnconfirmed',
          }
        : undefined,
    })
  }

  const clearFeedback = () => {
    if (active.current || retryRef.current?.kind === 'create') return
    completedJobId.current = undefined
    retryRef.current = undefined
    setState((current) => ({
      ...current,
      feedback: undefined,
      downloadJobId: undefined,
      retry: retryRef.current,
    }))
  }

  return {
    ...state,
    busy: state.phase !== 'idle',
    conditionsLocked: state.phase !== 'idle' || state.retry?.kind === 'create',
    isLocked: () => active.current || retryRef.current?.kind === 'create',
    submit,
    downloadExport,
    retryExport: retry,
    startNewReport,
    clearFeedback,
  }
}
